import React, {FC, useEffect, useMemo, useState} from 'react'
import {FormikProps} from 'formik'
import Select, {components} from 'react-select'
import {toast} from 'react-toastify'
import {useAppDispatch} from 'src/redux/storeHooks'
import {getCustomFieldsListAsync, getCustomFieldValueListAsync} from 'src/redux/actions/omsPageActions'
import {OmsInfoLabel, OmsTooltip} from './OmsTooltip'

// ─── Props ────────────────────────────────────────────────────────────────────

interface OrderCustomFieldsProps {
  values: any
  setFormikState: FormikProps<any>['setFormikState']
  mode: 'create' | 'edit'
  // Lifts the loaded ORDER field meta up so the parent can build the save payload
  onFieldsMetaLoaded?: (fields: any[]) => void
  // Edit mode: raw rows from GET /oms/orders/{id}/custom-fields → results.data
  orderCustomFieldsRaw?: any[]
  disabled?: boolean
  isSyncedOrder?: boolean
  /** Override tooltip shown when fields are disabled (e.g. view-only vs warehouse-only). */
  disabledTip?: string
}

// ─── Shared OMS-scoped classes (match CreateOmsOrder / oms-order.css) ────────

const inputCls = 'oms-input'

const NOT_PROVIDED_TIP = 'This field was not provided by the source platform, so it is shown as read-only.'
const VIEW_ONLY_TIP = 'This order is view-only and custom fields cannot be edited.'

const parseCheckboxValue = (input: any): boolean => {
  if (typeof input === 'boolean') return input
  if (typeof input === 'number') return input === 1
  if (typeof input === 'string') {
    const n = input.trim().toLowerCase()
    return n === 'true' || n === '1' || n === 'yes'
  }
  if (input && typeof input === 'object') {
    return parseCheckboxValue(
      input?.value ?? input?.name ?? input?.label ?? input?.api_name ?? input?.field_value
    )
  }
  return false
}

const extractList = (res: any): any[] =>
  res?.data?.results?.data ||
  res?.results?.data ||
  res?.data?.data ||
  res?.data ||
  res?.results ||
  res ||
  []

const defaultForField = (f: any): any => {
  if (f.input_type === 'MULTISELECT') {
    const arr = Array.isArray(f.default_value)
      ? f.default_value
      : f.default_value && typeof f.default_value === 'object'
      ? [f.default_value]
      : []
    return arr.map((x: any) => x.id)
  }
  if (f.input_type === 'SELECT') {
    const arr = Array.isArray(f.default_value)
      ? f.default_value
      : f.default_value && typeof f.default_value === 'object'
      ? [f.default_value]
      : []
    return arr[0] ? String(arr[0].id) : ''
  }
  if (f.input_type === 'CHECKBOX') return parseCheckboxValue(f.default_value)
  return f.default_value?.name ?? ''
}

// ─── react-select checkbox option for MULTISELECT ────────────────────────────

const CheckboxOption = (props: any) => {
  const {isSelected, label} = props
  return (
    <components.Option {...props}>
      <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
        <input type='checkbox' checked={isSelected} readOnly style={{pointerEvents: 'none'}} />
        <span>{label}</span>
      </div>
    </components.Option>
  )
}

// ─── Component ────────────────────────────────────────────────────────────────

const OrderCustomFields: FC<OrderCustomFieldsProps> = ({
  values,
  setFormikState,
  mode,
  onFieldsMetaLoaded,
  orderCustomFieldsRaw,
  disabled = false,
  isSyncedOrder = false,
  disabledTip,
}) => {
  const dispatch = useAppDispatch()

  const [fieldsMeta, setFieldsMeta] = useState<any[]>([])
  const [metaLoading, setMetaLoading] = useState(false)
  const [customFieldValuesById, setCustomFieldValuesById] = useState<Record<number, any[]>>({})
  const [loadingFieldValues, setLoadingFieldValues] = useState<Record<number, boolean>>({})

  // ── 1) Load ORDER field meta ──────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setMetaLoading(true)
      try {
        const res = await dispatch(
          getCustomFieldsListAsync({
            paramData: {page: 1, limit: 100, search: '', isActive: true, sort_by: '', sort_order: '', owner: 'ORDER'},
          })
        ).unwrap()

        if (cancelled) return
        const list = extractList(res)
        setFieldsMeta(list)
        onFieldsMetaLoaded?.(list)
      } catch (err: any) {
        if (!cancelled) {
          console.error(err)
          toast.error(err?.data?.message || 'Failed to load order custom fields')
        }
      } finally {
        if (!cancelled) setMetaLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── 2) Seed formik custom_fields keys (create defaults / edit prefill) ─────
  useEffect(() => {
    if (!fieldsMeta.length) return

    const rowByFieldId = new Map<number, any>()
    ;(orderCustomFieldsRaw || []).forEach((r: any) => {
      const fid = Number(r?.platform_field ?? r?.platform_field_id ?? r?.field_id ?? r?.platform_field?.id)
      if (fid) rowByFieldId.set(fid, r)
    })

    setFormikState((prev: any) => {
      const existing = prev.values.custom_fields || {}
      const next = {...existing}

      fieldsMeta.forEach((f: any) => {
        const fieldId = Number(f.id)
        if (!fieldId) return

        // Ensure key exists with default first
        if (next[fieldId] === undefined) next[fieldId] = defaultForField(f)

        if (mode !== 'edit') return
        const row = rowByFieldId.get(fieldId)
        if (!row) return

        if (f.input_type === 'MULTISELECT') {
          let ids = row?.platform_field_data_ids
          if (typeof ids === 'string') {
            ids = ids.split(',').map((x: string) => Number(x.trim())).filter((n: number) => !Number.isNaN(n))
          } else if (Array.isArray(ids)) {
            ids = ids.map((x: any) => Number(x)).filter((n: number) => !Number.isNaN(n))
          } else if (ids != null) {
            ids = [Number(ids)].filter((n: number) => !Number.isNaN(n))
          } else if (row?.platform_field_data != null) {
            const one = Number(row.platform_field_data)
            ids = Number.isNaN(one) ? [] : [one]
          } else {
            ids = []
          }
          next[fieldId] = [...new Set(ids)]
        } else if (f.input_type === 'SELECT') {
          const id = row?.platform_field_data_id ?? row?.platform_field_data
          next[fieldId] = id != null ? String(id) : ''
        } else if (f.input_type === 'CHECKBOX') {
          next[fieldId] = parseCheckboxValue(row?.api_code ?? row?.api_name ?? row?.value ?? row?.field_value)
        } else {
          next[fieldId] = row?.api_code ?? row?.api_name ?? row?.value ?? ''
        }
      })

      return {...prev, values: {...prev.values, custom_fields: next}}
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsMeta, mode, orderCustomFieldsRaw])

  // ── 3) Load value lists for SELECT / MULTISELECT ──────────────────────────
  useEffect(() => {
    const selectable = (fieldsMeta || []).filter(
      (f: any) => (f.input_type === 'SELECT' || f.input_type === 'MULTISELECT') && f.isActive && f.owner === 'ORDER'
    )
    if (!selectable.length) return

    selectable.forEach((field: any) => {
      const fieldId = Number(field.id)
      if (!fieldId) return
      if (customFieldValuesById[fieldId]?.length) return
      if (loadingFieldValues[fieldId]) return

      setLoadingFieldValues((prev) => ({...prev, [fieldId]: true}))
      dispatch(getCustomFieldValueListAsync({id: fieldId}))
        .unwrap()
        .then((res: any) => {
          const list = extractList(res)
          const normalized = (list || [])
            .map((x: any) => ({
              id: x?.id ?? x?.value_id ?? x?.platform_value_id,
              name: x?.name ?? x?.api_code ?? x?.value ?? x?.display_name ?? x?.label,
            }))
            .filter((x: any) => x.id != null)
          setCustomFieldValuesById((prev) => ({...prev, [fieldId]: normalized}))
        })
        .catch((err: any) => console.error(err))
        .finally(() => setLoadingFieldValues((prev) => ({...prev, [fieldId]: false})))
    })
  }, [dispatch, fieldsMeta, customFieldValuesById, loadingFieldValues])

  const activeFields = useMemo(
    () => (fieldsMeta || []).filter((f: any) => f.isActive && f.owner === 'ORDER'),
    [fieldsMeta]
  )

  const updateCustomFieldValue = (id: number, value: any) => {
    setFormikState((prev: any) => ({
      ...prev,
      values: {
        ...prev.values,
        custom_fields: {...(prev.values.custom_fields || {}), [id]: value},
      },
    }))
  }

  // ── 4) Render inputs by type ──────────────────────────────────────────────
  const renderInput = (field: any, isNotProvided: boolean) => {
    const value = values.custom_fields?.[field.id]
    const currentDisabled = disabled || isNotProvided
    const placeholder = isNotProvided ? 'Not provided' : undefined
    const lockedTip = isNotProvided ? NOT_PROVIDED_TIP : disabled ? (disabledTip || VIEW_ONLY_TIP) : undefined

    const wrapLocked = (node: React.ReactElement) =>
      lockedTip ? (
        <OmsTooltip tip={lockedTip} inline={false}>
          {node}
        </OmsTooltip>
      ) : (
        node
      )

    switch (field.input_type) {
      case 'NUMBER':
        return wrapLocked(
          <input type='number' className={inputCls} placeholder={placeholder} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
      case 'EMAIL':
        return wrapLocked(
          <input type='email' className={inputCls} placeholder={placeholder} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
      case 'DATE':
        return wrapLocked(
          <input type='date' className={`${inputCls} ${!value ? 'is-empty-date' : ''}`} placeholder={placeholder} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
      case 'DATE_TIME':
        return wrapLocked(
          <input type='datetime-local' className={`${inputCls} ${!value ? 'is-empty-date' : ''}`} placeholder={placeholder} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
      case 'TIME':
        return wrapLocked(
          <input type='time' className={`${inputCls} ${!value ? 'is-empty-date' : ''}`} placeholder={placeholder} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
      case 'URL':
        return wrapLocked(
          <input type='url' className={inputCls} placeholder={placeholder || 'https://...'} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
      case 'CHECKBOX':
        return wrapLocked(
          <label className='inline-flex items-center gap-[8px] text-[13px] text-[#333333]' style={{cursor: currentDisabled ? 'not-allowed' : 'pointer'}}>
            <input type='checkbox' checked={parseCheckboxValue(value)} onChange={(e) => updateCustomFieldValue(field.id, e.target.checked)} disabled={currentDisabled} />
            <span className={currentDisabled ? 'text-gray-500' : ''}>{field.display_name}</span>
          </label>
        )
      case 'SELECT': {
        const options = (customFieldValuesById[field.id] || []).map((o: any) => ({value: String(o.id), label: o.name}))
        const selected = options.find((o: any) => String(o.value) === String(value ?? '')) || null
        return wrapLocked(
          <Select
            classNamePrefix='oms-rs'
            options={options}
            value={selected}
            onChange={(opt: any) => updateCustomFieldValue(field.id, opt?.value || '')}
            placeholder={placeholder || (loadingFieldValues[field.id] ? 'Loading…' : 'Select…')}
            isClearable
            isDisabled={currentDisabled}
            menuPlacement='auto'
            maxMenuHeight={260}
            menuShouldScrollIntoView={false}
          />
        )
      }
      case 'MULTISELECT': {
        const options = (customFieldValuesById[field.id] || []).map((o: any) => ({value: o.id, label: o.name}))
        const selected = options.filter((o: any) => (value ?? []).includes(o.value))
        return wrapLocked(
          <Select
            classNamePrefix='oms-rs'
            isMulti
            options={options}
            value={selected}
            onChange={(items: any) => updateCustomFieldValue(field.id, (items || []).map((x: any) => x.value))}
            placeholder={placeholder || (loadingFieldValues[field.id] ? 'Loading…' : 'Select…')}
            isDisabled={currentDisabled}
            components={{Option: CheckboxOption}}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            menuPlacement='auto'
            maxMenuHeight={260}
            menuShouldScrollIntoView={false}
          />
        )
      }
      case 'TEXT':
      default:
        return wrapLocked(
          <input className={inputCls} placeholder={placeholder} value={value ?? ''} onChange={(e) => updateCustomFieldValue(field.id, e.target.value)} disabled={currentDisabled} />
        )
    }
  }

  const getFieldLabelTip = (field: any, isNotProvided: boolean) => {
    if (isNotProvided) {
      return (
        <>
          Custom order field: {field.display_name}
          <br />
          <em>{NOT_PROVIDED_TIP}</em>
        </>
      )
    }
    if (disabled) {
      return (
        <>
          Custom order field: {field.display_name}
          <br />
          <em>{disabledTip || VIEW_ONLY_TIP}</em>
        </>
      )
    }
    return `Custom order field: ${field.display_name}`
  }

  if (metaLoading && !activeFields.length) {
    return <div className='text-[13px] text-[#6c757d]'>Loading custom fields…</div>
  }

  if (!activeFields.length) {
    return <div className='text-[13px] text-[#6c757d]'>No custom fields configured for orders.</div>
  }

  return (
    <div className='grid grid-cols-2 gap-[20px] content-start'>
      {activeFields.map((field: any) => {
        const val = values.custom_fields?.[field.id]
        const isEmpty = val == null || val === '' || (Array.isArray(val) && val.length === 0) || (field.input_type === 'CHECKBOX' && !parseCheckboxValue(val))
        const isNotProvided = isSyncedOrder && isEmpty

        return (
          <div
            key={field.id}
            className={isNotProvided ? 'oms-field--not-provided' : undefined}
          >
            {field.input_type !== 'CHECKBOX' && (
              <OmsInfoLabel
                text={field.display_name}
                required={!!field.isRequired}
                tip={getFieldLabelTip(field, isNotProvided)}
              />
            )}
            {renderInput(field, isNotProvided)}
          </div>
        )
      })}
    </div>
  )
}

export default OrderCustomFields
